import { Form as FormikForm, Formik } from "formik";
import useFetch from "hooks/useFetch";
import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  FormikTextarea,
  FormikTextField,
  Icon,
  PageHeader,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import styles from "./AdminPushPage.module.scss";

interface AdminPushNotificationForm {
  title: string;
  body: string;
  imgurl: string;
}

const MAX_CHAR_LIMIT = 255;

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Tittel er påkrevd"),
  body: Yup.string()
    .required("Melding er påkrevd")
    .max(MAX_CHAR_LIMIT, `Melding kan ikke overstige ${MAX_CHAR_LIMIT} tegn`),
  imgurl: Yup.string().url("Ugyldig URL").nullable(),
});

const AdminPushPage: React.FC = () => {
  const { isLoading, error, execute } = useFetch<any>();
  const toast = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formValues, setFormValues] =
    useState<AdminPushNotificationForm | null>(null);

  const handleSubmit = (values: AdminPushNotificationForm) => {
    setFormValues(values);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!formValues) return;

    const payload = {
      title: formValues.title,
      body: formValues.body,
      imgurl: formValues.imgurl || null,
    };

    const response = await execute(
      "/api/v1/notification/send-to-admins",
      "POST",
      payload,
    );

    if (response) {
      toast.success(`Push-melding sendt (ID: ${response.id})`);
      setFormValues(null);
      setShowConfirmModal(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Push-meldinger"
        subtitle="Send varsler til alle brukere av iBønn, eller til alle moskéadminer."
      />
      <SectionTabs />

      <Formik
        initialValues={{
          title: "",
          body: "",
          imgurl: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, values, setFieldValue }) => (
          <FormikForm className={styles.layout} noValidate>
            <Card title="Ny melding til alle admins">
              <FormikTextField
                name="title"
                label="Tittel"
                required
                placeholder="Skriv tittel …"
              />
              <FormikTextarea
                name="body"
                label="Melding"
                required
                rows={3}
                placeholder="Skriv meldingen …"
                maxLength={MAX_CHAR_LIMIT}
                maxChars={MAX_CHAR_LIMIT}
                onChange={(e) =>
                  setFieldValue("body", e.target.value.slice(0, MAX_CHAR_LIMIT))
                }
              />
              <FormikTextField
                name="imgurl"
                label="Bildelenke (valgfri)"
                placeholder="https://…"
                help="Må være en gyldig URL."
              />
              {error && <Alert tone="error">Feilmelding: {error.text}</Alert>}
              <div className={styles.actions}>
                <Button
                  icon={<Icon name="bell" size={16} />}
                  onClick={() => handleSubmit(values)}
                  loading={isLoading}
                  disabled={!(isValid && dirty)}
                >
                  Send til admins
                </Button>
              </div>
            </Card>

            <section className={styles.preview} aria-label="Forhåndsvisning">
              <span className={styles.previewLabel}>FORHÅNDSVISNING</span>
              <div className={styles.notification}>
                <span className={styles.appIcon}>
                  <Icon name="moon" size={18} />
                </span>
                <span className={styles.notificationText}>
                  <b>{values.title || "Tittel"}</b>
                  <span>{values.body || "Meldingen vises her."}</span>
                </span>
              </div>
            </section>
          </FormikForm>
        )}
      </Formik>

      <ConfirmDialog
        open={showConfirmModal}
        title="Send til alle admins?"
        confirmLabel="Bekreft og send"
        busy={isLoading}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmModal(false)}
      >
        «{formValues?.title}» sendes nå til <b>alle admins</b>. Dette kan ikke
        angres.
      </ConfirmDialog>
    </>
  );
};

export default AdminPushPage;
